import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { ApiProblem, initI18n } from '@cowoo/shared';
import i18next from 'i18next';
import { toast } from './ui/toast';
import { hydrateSession } from './lib/auth';
import { routes } from './routes';
import './index.css';

initI18n('web');

// A token that survived the reload is adopted before the first render, so the
// gate does not bounce a signed-in admin to `/login` on the way past.
hydrateSession();

/**
 * 10-web-admin § 20 — `403 forbidden` and `429` are answered with a toast,
 * whatever screen they hit. Everything else stays with the region that asked.
 */
const onProblem = (error: unknown): void => {
  if (!(error instanceof ApiProblem)) return;
  if (error.status === 403 || error.status === 429) toast(i18next.t(error.i18nKey), { tone: 'danger' });
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onProblem }),
  mutationCache: new MutationCache({ onError: onProblem }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (count, error) =>
        !(error instanceof ApiProblem && error.status >= 400 && error.status < 500) && count < 2,
      // Admin lists keep their previous page while a filter is applied.
      placeholderData: (previous: unknown) => previous,
    },
    mutations: { retry: 0 },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={createBrowserRouter(routes)} />
    </QueryClientProvider>
  </StrictMode>,
);
